// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// NC component (NodeControl Design System). Mirrored from the design system;
// adjust the design system, not this AU copy.
//
// NCStagePanel — a sticky side-column overview of a gated multi-stage flow's
// progress: the same stage data `NCStepper` renders inline, pulled into its
// own column so overall progress stays visible without scrolling past it.
// See nc-components/usage-notes/stage-panel.md for the full rules (icon
// language, row spacing, locked-stage treatment, why the panel stays
// bg-background rather than bg-card) and layouts/stage-panel-layout.md for
// where this component sits in the page — this component renders only the
// panel's own content; the sticky two-column positioning is that layout's
// job, not this component's.
//
// Deliberately reuses NCStepperStage/NCStepperStageStatus from ./stepper
// rather than declaring a parallel type: a stage panel and a Stepper render
// the exact same stage data, just in a different place
// (nc-components/usage-notes/stage-panel.md, "Stage panel vs. Stepper") — an
// AU should be able to pass the same `stages` array to either without
// reshaping it.
//
// Row titles are one rung below the panel's own title (text-nc-2 vs.
// text-nc-3) and stay plain weight (font-nc-1) at every status, unlike
// Stepper's own text-nc-3/font-nc-2 rows: Stepper lives in the wide main
// content column, this panel's own column is clamped to 220-320px
// (layouts/stage-panel-layout.md), where Stepper's exact type scale reads
// oversized and, once mostly `done`, as a solid block of bold text next to
// the plain-weight NCStatTileGrid content this panel typically sits beside.
// Status is carried by icon shape and, for `failed`, color — never by
// weight or a Card surface (see the bg-background rule above).

import { CheckCircle2, Circle, Loader2, Lock, MinusCircle, XCircle } from "lucide-react";

import { cn } from "@/lib/utils";

import type { NCStepperStage, NCStepperStageStatus } from "./stepper";

export interface NCStagePanelProps {
  /** The panel's own first child, at the same rung NCSectionHeader's title
   *  uses (e.g. "Lookup stages"). */
  title: string;
  stages: NCStepperStage[];
  className?: string;
}

const TITLE_TONE: Record<NCStepperStageStatus, string> = {
  pending: "font-nc-1 text-muted-foreground",
  skipped: "font-nc-1 text-muted-foreground",
  active: "font-nc-1 text-foreground",
  done: "font-nc-1 text-foreground",
  failed: "font-nc-1 text-destructive",
};

function StageIcon({ status }: { status: NCStepperStageStatus }) {
  switch (status) {
    case "done":
      return <CheckCircle2 className="size-5 text-nc-success" aria-hidden />;
    case "active":
      return <Loader2 className="size-5 animate-spin text-primary" aria-hidden />;
    case "failed":
      return <XCircle className="size-5 text-destructive" aria-hidden />;
    case "skipped":
      return <MinusCircle className="size-5 text-muted-foreground" aria-hidden />;
    case "pending":
    default:
      return <Circle className="size-5 text-muted-foreground" aria-hidden />;
  }
}

export function NCStagePanel({ title, stages, className }: NCStagePanelProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-nc-3 rounded-[var(--radius)] border border-border bg-background p-nc-4",
        className,
      )}
    >
      <span className="text-nc-3 font-nc-2 leading-nc-1 text-foreground">{title}</span>

      <ol className="flex flex-col gap-nc-2" aria-label="Stage overview">
        {stages.map((stage) => {
          const locked = stage.status === "pending" && Boolean(stage.lockedReason);

          return (
            <li key={stage.id} className="flex gap-nc-3">
              <StageIcon status={stage.status} />
              <div className="min-w-0 flex-1">
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className={cn("text-nc-2 leading-nc-1", TITLE_TONE[stage.status])}>
                    {stage.title}
                  </span>
                  {locked ? (
                    <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  ) : null}
                </span>
                {locked && stage.lockedReason ? (
                  <p className="text-nc-1 leading-nc-2 text-muted-foreground">
                    {stage.lockedReason}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
